import { logger } from '../../utils/Logger';
import ConnectionState from '../enums/ConnectionState';
import { RecoType } from '../enums/RecoType';
import Member from '../models/Member';
import Message from '../models/Message';
import User from '../models/User';
import { RecoUser } from '../packets/RecoItem';
import RelayItem from '../packets/RelayItem';
import { SMSChannelPkt } from '../packets/SMSChannel';
import { RelayCallbacks } from './relay';

export type ConnectionListener = (connectionState: ConnectionState) => void;
export type SMSDataHandler = (item: SMSChannelPkt) => void;
export type RecoHandler = (users: Map<string, RecoUser>, hasMore: boolean, type: RecoType) => void;
export type PresenceListener = (user: string, status: string) => void;
export type ProgressListener = (message: Message, mediaIdx: number, progress: number) => void;
export type MessageListener = (message: Message) => void;
export type TypingListener = (channelId: string, from: User | Member, typing: boolean) => void;
export type RelayItemListener = (item: RelayItem) => void;

export class CallbackHandler {
  public static shared = new CallbackHandler();

  private constructor() {}

  private externalEmitter?: (event: string, payload: any) => void;

  setExternalEmitter = (emitter?: (event: string, payload: any) => void) => {
    this.externalEmitter = emitter;
  };

  private connectionListeners = new Array<ConnectionListener>();

  registerConnectionListener = (handler: ConnectionListener) => {
    this.connectionListeners.push(handler);
  };

  unregisterConnectionListener = (handler: ConnectionListener) => {
    this.connectionListeners = this.connectionListeners.filter((x) => x !== handler);
  };

  callOnConnectionStateChanged = async (state: ConnectionState) => {
    logger.leaveBreadcrumb(
      'callOnConnectionStateChanged',
      {
        state: state
      },
      'state'
    );

    this.connectionListeners.forEach((handler) => {
      handler(state);
    });
    this.externalEmitter?.('connectionStateChanged', { state });
  };

  // typing
  private typingListeners = new Array<TypingListener>();

  registerTypingListener = (handler: TypingListener) => {
    this.typingListeners.push(handler);
  };

  unregisterTypingListener = (handler: TypingListener) => {
    this.typingListeners = this.typingListeners.filter((x) => x !== handler);
  };

  callOnTypingStateChanged = async (channelId: string, from: User | Member, typing: boolean) => {
    this.typingListeners.forEach((handler) => {
      handler(channelId, from, typing);
    });
    this.externalEmitter?.('typingStateChanged', { channelId, from, typing });
  };

  // sms
  private smsHandlers = new Array<SMSDataHandler>();

  registerSMSHandler = (handler: SMSDataHandler) => {
    this.smsHandlers.push(handler);
  };

  unregisterSMSHandler = (handler: SMSDataHandler) => {
    this.smsHandlers = this.smsHandlers.filter((x) => x !== handler);
  };

  callOnSMSChannelData = async (item: SMSChannelPkt) => {
    this.smsHandlers.forEach((handler) => {
      handler(item);
    });
    this.externalEmitter?.('smsChannelData', { item });
  };

  //reco handler
  private recoHandler = new Array<RecoHandler>();

  registerRecoHandler = (handler: RecoHandler) => {
    this.recoHandler.push(handler);
  };

  unregisterRecoHandler = (handler: RecoHandler) => {
    this.recoHandler = this.recoHandler.filter((x) => x !== handler);
  };

  callOnRecoData = async (users: Map<string, RecoUser>, hasMore: boolean, type: RecoType) => {
    this.recoHandler.forEach((handler) => {
      handler(users, hasMore, type);
    });
    this.externalEmitter?.('recoData', { users, hasMore, type });
  };

  //
  private relayHandler = new Array<RelayCallbacks>();

  registerRelayHandler = (handler: RelayCallbacks) => {
    this.relayHandler.push(handler);
  };

  unregisterRelayHandler = (handler: RelayCallbacks) => {
    this.relayHandler = this.relayHandler.filter((x) => x !== handler);
  };

  callOnRelayData = async (items: Array<RelayItem>, hasMore: boolean) => {
    this.relayHandler.forEach((handler) => {
      handler.onRelayData?.(items, hasMore);
    });
    this.externalEmitter?.('relayData', { items, hasMore });
  };

  private relayItemListeners = new Array<RelayItemListener>();

  registerRelayItemListener = (handler: RelayItemListener) => {
    this.relayItemListeners.push(handler);
  };

  unregisterRelayItemListener = (handler: RelayItemListener) => {
    this.relayItemListeners = this.relayItemListeners.filter((x) => x !== handler);
  };

  callOnRelayItem = async (item: RelayItem) => {
    this.relayItemListeners.forEach((x) => x(item));
    this.externalEmitter?.('relayItem', { item });
  };

  private presenceListeners = new Array<PresenceListener>();

  registerPresenceListener = (handler: PresenceListener) => {
    this.presenceListeners.push(handler);
  };

  unregisterPresenceListener = (handler: PresenceListener) => {
    this.presenceListeners = this.presenceListeners.filter((x) => x !== handler);
  };

  callOnPresenceReceived = async (uuid: string, status: string) => {
    this.presenceListeners.forEach((x) => x(uuid, status));
    this.externalEmitter?.('presenceReceived', { uuid, status });
  };

  ///

  private progressListeners = new Array<ProgressListener>();

  registerUploadProgressListener = (handler: ProgressListener) => {
    this.progressListeners.push(handler);
  };

  unregisterUploadProgressListener = (handler: ProgressListener) => {
    this.progressListeners = this.progressListeners.filter((x) => x !== handler);
  };

  callOnProgressUpdated = async (message: Message, mediaIdx: number, progress: number) => {
    this.progressListeners.forEach((x) => x(message, mediaIdx, progress));
    this.externalEmitter?.('progressUpdated', { message, mediaIdx, progress });
  };

  private messageListeners = new Array<MessageListener>();
  private channelMessageListeners = new Map<string, Set<MessageListener>>();

  registerOnMessageAddedListener = (handler: MessageListener, channelId?: string) => {
    if (channelId) {
      let bucket = this.channelMessageListeners.get(channelId);
      if (!bucket) {
        bucket = new Set();
        this.channelMessageListeners.set(channelId, bucket);
      }
      bucket.add(handler);
      return;
    }
    this.messageListeners.push(handler);
  };

  unregisterOnMessageAddedListener = (handler: MessageListener, channelId?: string) => {
    if (channelId) {
      const bucket = this.channelMessageListeners.get(channelId);
      if (bucket) {
        bucket.delete(handler);
        if (bucket.size === 0) {
          this.channelMessageListeners.delete(channelId);
        }
      }
      return;
    }
    this.messageListeners = this.messageListeners.filter((x) => x !== handler);
  };

  callOnMessageAdded = async (message: Message) => {
    const channelBucket = this.channelMessageListeners.get(message.channel);
    if (channelBucket) {
      channelBucket.forEach((x) => x(message));
    }
    this.messageListeners.forEach((x) => x(message));
    this.externalEmitter?.('messageAdded', { message });
  };
}
